import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import type { LobbySnapshot, PublicPlayer } from "@themix/shared";
import { getSocket } from "../lib/socket";
import { isAudioUnlocked, pauseClip, playClip, setClipTime, stopClip, unlockAudio } from "../lib/audio";
import {
  mergeLobbySnapshot,
  readStoredPlayer,
  hasCredentialsForRoom,
  hasSeatForRoom,
  saveStoredPlayer,
  clearStoredPlayer,
  useLiveRemain,
  useListenLock,
  useRetryLock,
  SYSTEM_REJECT_REASONS,
  type StoredPlayer,
} from "../lib/play";
import { AnswerDock, ThemeStrip } from "../components/PlayDock";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";

export default function Lobby() {
  const nav = useNavigate();
  const { code = "" } = useParams();
  const [params, setParams] = useSearchParams();
  const wantSpectate = params.get("spectate") === "1";
  const [snap, setSnap] = useState<LobbySnapshot | null>(null);
  const [answer, setAnswer] = useState("");
  const [msg, setMsg] = useState("");
  const [needSound, setNeedSound] = useState(!isAudioUnlocked());
  const [showRank, setShowRank] = useState(false);
  const [joined, setJoined] = useState(false);
  const [retryUntil, setRetryUntil] = useState(0);
  const remain = useLiveRemain(snap);
  const { listenLock, listenLeft } = useListenLock(
    snap?.round?.roundId,
    snap?.round?.startedAt,
    snap?.phase === "playing",
  );
  const { retryLock, retryLeft } = useRetryLock(retryUntil, snap?.phase === "playing");
  const inputRef = useRef<HTMLInputElement>(null);
  const stored = useRef<StoredPlayer | null>(readStoredPlayer());
  const quitting = useRef(false);
  const spectate = wantSpectate && !hasCredentialsForRoom(code);
  const readyToConnect = spectate || joined || hasSeatForRoom(code);

  function leaveForHome() {
    quitting.current = true;
    stopClip();
    stored.current = null;
    clearStoredPlayer();
    nav("/", { replace: true });
  }

  function quitLobby() {
    if (snap?.you?.isHost && !window.confirm("Fermer le salon pour tout le monde ?")) return;
    quitting.current = true;
    getSocket().emit("lobby:quit", {}, () => {
      leaveForHome();
    });
  }

  useEffect(() => {
    if (!readyToConnect) return;
    stored.current = readStoredPlayer();
    const socket = getSocket();

    const onSnap = (s: LobbySnapshot) => setSnap((prev) => mergeLobbySnapshot(prev, s));
    const onJoined = (payload: { player?: PublicPlayer }) => {
      if (!payload.player) return;
      setSnap((prev) => {
        if (!prev) return prev;
        const p = payload.player!;
        const exists = prev.players.some((x) => x.id === p.id);
        return {
          ...prev,
          players: exists ? prev.players.map((x) => (x.id === p.id ? { ...x, ...p, connected: true } : x)) : [...prev.players, { ...p, connected: true }],
        };
      });
    };
    const onLeft = (payload: { playerId?: string; temporary?: boolean }) => {
      if (!payload.playerId) return;
      setSnap((prev) => {
        if (!prev) return prev;
        if (payload.temporary === false) {
          return { ...prev, players: prev.players.filter((p) => p.id !== payload.playerId) };
        }
        return { ...prev, players: prev.players.map((p) => (p.id === payload.playerId ? { ...p, connected: false } : p)) };
      });
    };
    const onUpdated = (payload: { playerId?: string; connected?: boolean; ready?: boolean; nickname?: string }) => {
      if (!payload.playerId) return;
      setSnap((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          players: prev.players.map((p) =>
            p.id === payload.playerId
              ? {
                  ...p,
                  connected: payload.connected ?? p.connected,
                  ready: payload.ready ?? p.ready,
                  nickname: payload.nickname ?? p.nickname,
                }
              : p,
          ),
        };
      });
    };

    let cancelled = false;
    function hello() {
      if (!spectate && !hasSeatForRoom(code) && !stored.current?.playerId) return;
      socket.emit("lobby:leave", {}, () => {
        if (cancelled) return;
        if (spectate) {
          socket.emit("lobby:spectate", { code }, (res: { error?: string; snapshot?: LobbySnapshot }) => {
            if (cancelled) return;
            if (res?.snapshot) setSnap((prev) => mergeLobbySnapshot(prev, res.snapshot!));
            if (res?.error) setMsg(res.error);
          });
          return;
        }
        const token = stored.current?.reconnectToken || stored.current?.token || "";
        const playerId = stored.current?.playerId;
        if (!playerId || !token) {
          setJoined(false);
          return;
        }
        socket.emit("lobby:hello", { code, playerId, token }, (res: { error?: string; snapshot?: LobbySnapshot }) => {
          if (cancelled) return;
          if (res?.snapshot) setSnap((prev) => mergeLobbySnapshot(prev, res.snapshot!));
          if (res?.error) {
            setMsg(res.error);
            stored.current = null;
            try {
              sessionStorage.removeItem("themix_player");
              localStorage.removeItem("themix_player");
            } catch {
              /* ignore */
            }
            setJoined(false);
          }
        });
      });
    }

    socket.on("lobby:snapshot", onSnap);
    socket.on("player:joined", onJoined);
    socket.on("player:left", onLeft);
    socket.on("player:updated", onUpdated);
    socket.on("answer:rejected", (r: { reason?: string; retryInMs?: number }) => {
      if (typeof r?.retryInMs === "number" && r.retryInMs > 0) {
        setRetryUntil(Date.now() + r.retryInMs);
      }
      if (r?.reason === "listen" || r?.reason === "rate") return;
      if (r?.reason === "cooldown") return;
      setMsg("Pas tout à fait…");
    });
    socket.on("server:error", (e: { message: string }) => setMsg(e.message));
    socket.on("lobby:closed", () => {
      quitting.current = true;
      stopClip();
      stored.current = null;
      clearStoredPlayer();
      nav("/", { replace: true });
    });
    socket.on("connect", hello);
    if (readyToConnect && socket.connected) hello();

    return () => {
      cancelled = true;
      if (!quitting.current) socket.emit("lobby:leave");
      socket.off("lobby:snapshot", onSnap);
      socket.off("player:joined", onJoined);
      socket.off("player:left", onLeft);
      socket.off("player:updated", onUpdated);
      socket.off("answer:rejected");
      socket.off("server:error");
      socket.off("lobby:closed");
      socket.off("connect", hello);
    };
  }, [code, spectate, readyToConnect]);

  useEffect(() => {
    if (!snap?.round || snap.phase === "paused" || snap.phase === "countdown" || needSound) {
      if (snap?.phase === "paused" || snap?.phase === "reveal" || snap?.phase === "finished") pauseClip();
      return;
    }
    if (snap.phase === "playing" && snap.round.audioUrl) {
      playClip(snap.round.audioUrl, snap.round.clipStartSeconds);
    }
    if (snap.phase === "reveal") stopClip();
  }, [snap?.phase, snap?.round?.roundId, needSound]);

  useEffect(() => {
    setAnswer("");
    setMsg("");
    setRetryUntil(0);
  }, [snap?.round?.roundId]);

  if (!spectate && !readyToConnect) {
    return (
      <JoinLobbyForm
        code={code.toUpperCase()}
        error={msg}
        onJoined={(seat) => {
          stored.current = seat;
          saveStoredPlayer(seat);
          setMsg("");
          setJoined(true);
        }}
        onSpectate={() => {
          setMsg("");
          setParams({ spectate: "1" }, { replace: true });
        }}
      />
    );
  }

  if (!snap) return <p className="pt-8">{msg || "Connexion au salon…"}</p>;

  const spectator = spectate || snap.spectator || !snap.you;
  const youHost = Boolean(snap.you?.isHost);
  const share = `${window.location.origin}/j/${snap.code}`;
  const ranked = [...snap.players].sort((a, b) => b.score - a.score || a.nickname.localeCompare(b.nickname, "fr"));
  const waiting = snap.phase === "lobby";
  const inMatch = snap.phase !== "lobby" && snap.phase !== "finished";
  const canAnswer = snap.phase === "playing" && !spectator;
  const keepComposer = !spectator && snap.phase !== "lobby" && snap.phase !== "finished";

  return (
    <div className="flex-1 min-h-0 grid lg:grid-cols-[minmax(0,1fr)_16rem] gap-4">
      <div className="relative flex flex-col min-w-0 min-h-0">
        {inMatch && snap.round && (
          <ThemeStrip
            theme={snap.round.theme}
            timer={<span className="font-display text-3xl tabular-nums">{remain}s</span>}
            actions={
              <>
                {youHost && snap.phase !== "paused" && (
                  <button
                    type="button"
                    className="rounded-full border border-cream/30 px-3 py-1 text-xs font-semibold"
                    onMouseDown={(e) => e.preventDefault()}
                    onPointerDown={(e) => e.preventDefault()}
                    onClick={() => getSocket().emit("game:pause")}
                  >
                    Pause
                  </button>
                )}
                {youHost && snap.phase === "paused" && (
                  <button
                    type="button"
                    className="rounded-full bg-mint text-ink px-3 py-1 text-xs font-semibold"
                    onMouseDown={(e) => e.preventDefault()}
                    onPointerDown={(e) => e.preventDefault()}
                    onClick={() => {
                      getSocket().emit("game:resume");
                      if (snap.round) setClipTime((snap.round.durationMs - snap.round.remainingMs) / 1000);
                    }}
                  >
                    Reprendre
                  </button>
                )}
                <button
                  type="button"
                  className="lg:hidden rounded-full border border-cream/30 px-3 py-1 text-xs font-semibold"
                  onMouseDown={(e) => e.preventDefault()}
                  onPointerDown={(e) => e.preventDefault()}
                  onClick={() => setShowRank((v) => !v)}
                  aria-expanded={showRank}
                >
                  {showRank ? "Fermer" : "Scores"}
                </button>
                <button
                  type="button"
                  className="rounded-full border border-pink/50 text-pink px-3 py-1 text-xs font-semibold"
                  onMouseDown={(e) => e.preventDefault()}
                  onPointerDown={(e) => e.preventDefault()}
                  onClick={() => quitLobby()}
                >
                  Quitter
                </button>
              </>
            }
          />
        )}
        {inMatch && showRank && (
          <div className="lg:hidden z-40 rounded-3xl bg-ink-2 p-4 shadow-xl max-h-[40%] overflow-y-auto">
            <p className="uppercase tracking-[0.18em] text-gold text-xs">Classement</p>
            <ol className="mt-3 grid gap-2">
              {ranked.map((p, i) => (
                <PlayerRow key={p.id} player={p} index={i} youId={snap.you?.id} youHost={youHost} phase={snap.phase} />
              ))}
            </ol>
          </div>
        )}
        <div className={`flex-1 min-h-0 overflow-y-auto flex flex-col gap-4 ${inMatch ? "py-1" : "py-3"}`}>
          {needSound && (
            <button
              type="button"
              className="rounded-2xl bg-gold text-ink py-3 font-semibold"
              onClick={() => {
                void unlockAudio();
                setNeedSound(false);
              }}
            >
              Activer le son
            </button>
          )}
          {spectator && waiting && (
            <p className="rounded-2xl border border-pink/40 px-4 py-2 text-sm text-pink">
              Mode spectateur — tu écoutes sans pouvoir répondre.
            </p>
          )}
          {snap.phase === "paused" && (
            <div className="rounded-2xl bg-pink/20 border border-pink px-3 py-2 text-center text-sm">
              Partie en pause
            </div>
          )}
          {waiting && (
            <header className="flex justify-between items-start">
              <div>
                <p className="text-xs uppercase tracking-widest opacity-70">Salon</p>
                <p className="font-display text-4xl">{snap.code}</p>
                <button type="button" className="text-sm underline opacity-80" onClick={() => void navigator.clipboard.writeText(share)}>
                  Copier le lien
                </button>
              </div>
              {youHost && (
                <button type="button" className="rounded-2xl bg-gold text-ink px-4 py-3 font-bold" onClick={() => getSocket().emit("game:start")}>
                  Lancer
                </button>
              )}
            </header>
          )}
          {(waiting || snap.phase === "finished") && (
            <button type="button" className="rounded-2xl border border-pink/50 text-pink py-3 font-semibold" onClick={() => quitLobby()}>
              {youHost ? "Fermer le salon" : "Quitter le lobby"}
            </button>
          )}
          {youHost && snap.phase === "finished" && (
            <button type="button" className="rounded-2xl bg-gold text-ink px-4 py-3" onClick={() => getSocket().emit("game:rematch")}>
              Revanche
            </button>
          )}

          {waiting && <p className="opacity-80">En attente du lancement. Les extraits démarreront ensemble.</p>}

          {snap.phase === "playing" && (
            <div className="hidden lg:block">
              <div className="eq" aria-hidden>
                <span />
                <span />
                <span />
                <span />
                <span />
              </div>
            </div>
          )}

          {snap.reveal && (snap.phase === "reveal" || snap.phase === "finished") && (
            <div className="rounded-3xl bg-ink-2 p-5 text-center">
              <p className="font-display text-3xl">{snap.reveal.canonicalAnswer}</p>
              <p className="mt-2 opacity-80">{snap.reveal.justification}</p>
              <p className="mt-2">
                {snap.reveal.title} — {snap.reveal.artist}
              </p>
              {snap.reveal.winner && <p className="mt-2 text-gold">+1 {snap.reveal.winner.nickname}</p>}
            </div>
          )}

          {(waiting || snap.phase === "finished") && (
            <>
              <p className="uppercase tracking-[0.18em] text-gold text-xs lg:hidden">Classement</p>
              <ol className="grid gap-2 lg:hidden">
                {ranked.map((p, i) => (
                  <PlayerRow key={p.id} player={p} index={i} youId={snap.you?.id} youHost={youHost} phase={snap.phase} />
                ))}
              </ol>
            </>
          )}
        </div>
        {keepComposer && (
          <AnswerDock
            value={answer}
            onChange={setAnswer}
            onSubmit={() =>
              new Promise<void>((resolve) => {
                const t = window.setTimeout(resolve, 8000);
                getSocket().emit("game:answer", { answer }, (r: { accepted?: boolean; reason?: string; retryInMs?: number }) => {
                  window.clearTimeout(t);
                  if (typeof r?.retryInMs === "number" && r.retryInMs > 0) {
                    setRetryUntil(Date.now() + r.retryInMs);
                  }
                  if (!r?.accepted && r?.reason && !SYSTEM_REJECT_REASONS.has(r.reason)) {
                    setAnswer("");
                  }
                  resolve();
                });
              })
            }
            locked={!canAnswer || listenLock}
            listenLock={canAnswer && listenLock}
            listenLeft={listenLeft}
            retryLock={canAnswer && retryLock}
            retryLeft={retryLeft}
            keepOpen
            inputRef={inputRef}
            message={msg}
          />
        )}
      </div>

      <aside className="hidden lg:block rounded-3xl bg-ink-2 p-4 h-fit">
        <p className="uppercase tracking-[0.18em] text-gold text-xs">Classement</p>
        <ol className="mt-3 grid gap-2">
          {ranked.map((p, i) => (
            <PlayerRow key={p.id} player={p} index={i} youId={snap.you?.id} youHost={youHost} phase={snap.phase} />
          ))}
        </ol>
      </aside>
    </div>
  );
}

function PlayerRow({
  player,
  index,
  youId,
  youHost,
  phase,
}: {
  player: PublicPlayer;
  index: number;
  youId?: string;
  youHost: boolean;
  phase: LobbySnapshot["phase"];
}) {
  return (
    <li
      className={`flex justify-between items-baseline gap-2 rounded-2xl px-3 py-2 ${
        player.id === youId ? "bg-gold/15" : "bg-ink"
      } ${player.connected ? "" : "opacity-50"}`}
    >
      <span className="min-w-0 truncate">
        <span className="opacity-60 mr-1">{index + 1}.</span>
        {player.nickname}
        {player.isHost ? " · hôte" : ""}
        {youHost && phase === "lobby" && !player.isHost && (
          <button type="button" className="ml-2" onClick={() => getSocket().emit("lobby:kick", { playerId: player.id })}>
            ×
          </button>
        )}
      </span>
      <span className="font-semibold shrink-0">{player.score}</span>
    </li>
  );
}

function JoinLobbyForm({
  code,
  error,
  onJoined,
  onSpectate,
}: {
  code: string;
  error?: string;
  onJoined: (seat: StoredPlayer) => void;
  onSpectate: () => void;
}) {
  const { user } = useAuth();
  const [nickname, setNickname] = useState(user?.nickname ?? "");
  const [err, setErr] = useState(error ?? "");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user?.nickname && !nickname) setNickname(user.nickname);
  }, [user, nickname]);

  useEffect(() => {
    if (error) setErr(error);
  }, [error]);

  async function go() {
    setErr("");
    const nick = nickname.trim();
    if (nick.length < 2) {
      setErr("Choisis un pseudo (2 caractères minimum).");
      return;
    }
    setBusy(true);
    await unlockAudio();
    try {
      const res = await api<{ playerId: string; token: string }>(`/api/lobbies/${code}/join`, {
        method: "POST",
        body: JSON.stringify({ nickname: nick }),
      });
      onJoined({ playerId: res.playerId, token: res.token, reconnectToken: res.token, code });
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Impossible de rejoindre");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="grid gap-4 pt-8 max-w-md">
      <h1 className="font-display text-4xl">Rejoindre</h1>
      <p className="opacity-80">
        Salon <span className="font-display text-gold tracking-widest">{code}</span>
      </p>
      <input
        className="rounded-2xl bg-cream text-ink px-4 py-3"
        placeholder="Ton pseudo"
        value={nickname}
        onChange={(e) => setNickname(e.target.value)}
        maxLength={24}
        autoFocus
        onKeyDown={(e) => {
          if (e.key === "Enter") void go();
        }}
      />
      {err && <p className="text-pink">{err}</p>}
      <button
        type="button"
        disabled={busy}
        className="rounded-3xl bg-gold text-ink py-4 font-bold text-xl disabled:opacity-50"
        onClick={() => void go()}
      >
        {busy ? "Entrée…" : "Entrer dans le salon"}
      </button>
      <button type="button" className="rounded-3xl border border-cream/30 py-3" onClick={onSpectate}>
        Juste regarder
      </button>
    </div>
  );
}
